import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useProject } from '../hooks/useProjects';
import { useMessages } from '../hooks/useMessages';
import { ChatPanel } from '../components/project/ChatPanel';
import { PreviewPanel } from '../components/project/PreviewPanel';
import { ProjectHeader } from '../components/project/ProjectHeader';
import { PaneSplitter } from '../components/project/PaneSplitter';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { CodeTab } from '../types/project.types';

// Chat-pane width for the app-shell workspace: a comfortable default (within
// the 380–420px range), resizable by dragging the splitter between the two
// panes. Bounds keep both panes usable at any viewport width.
const CHAT_PANE_DEFAULT = 400;
const CHAT_PANE_MIN = 320;
const CHAT_PANE_MAX_RATIO = 0.6;
// Distinct from ui_1's removed `werk42_split_ratio` key (which stored a
// 0–1 ratio) — this stores a pixel width, so a stale ratio value left over
// in a returning user's browser is never misread as a pixel width.
const CHAT_WIDTH_STORAGE_KEY = 'werk42_chat_pane_width_px';

function clampChatWidth(width: number, containerWidth: number): number {
  const max = Math.max(containerWidth * CHAT_PANE_MAX_RATIO, CHAT_PANE_MIN);
  return Math.min(Math.max(width, CHAT_PANE_MIN), max);
}

function readStoredChatWidth(): number {
  const stored = window.localStorage.getItem(CHAT_WIDTH_STORAGE_KEY);
  const parsed = stored ? Number(stored) : NaN;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : CHAT_PANE_DEFAULT;
}

type MobileTab = 'chat' | 'preview';

export function ProjectPage() {
  const { t } = useTranslation('project');
  const { projectId } = useParams<{ projectId: string }>();
  const { data: project, isLoading, error } = useProject(projectId);
  const { data: messages } = useMessages(projectId);
  const hasFiles = messages?.some((m) => m.role === 'assistant') ?? false;

  // Track whether we're on a mobile viewport.
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Mobile tab state — resets to 'chat' on project navigation.
  const [mobileTab, setMobileTab] = useState<MobileTab>('chat');
  // "Preview ready" badge: shown on Preview tab when a build completes while on Chat tab.
  const [previewReady, setPreviewReady] = useState(false);

  // Which code file is open in the PreviewPanel (null = live preview iframe).
  const [activeCodeFile, setActiveCodeFile] = useState<CodeTab | null>(null);

  // Resizable chat-pane width (desktop split view only).
  const splitContainerRef = useRef<HTMLDivElement>(null);
  const [chatWidth, setChatWidth] = useState<number>(() =>
    clampChatWidth(readStoredChatWidth(), window.innerWidth)
  );

  // Re-clamp on viewport resize so the split stays valid (e.g. window
  // narrowed after the width was set at a wider one). Skipped on mobile:
  // the split isn't rendered there (single full-width pane via tabs), so a
  // narrow phone viewport must never shrink the desktop width preference.
  useEffect(() => {
    if (isMobile) return;
    function handleResize() {
      const containerWidth = splitContainerRef.current?.clientWidth ?? window.innerWidth;
      setChatWidth((w) => clampChatWidth(w, containerWidth));
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile]);

  function applyChatWidthDelta(deltaX: number) {
    const containerWidth = splitContainerRef.current?.clientWidth ?? window.innerWidth;
    setChatWidth((w) => clampChatWidth(w + deltaX, containerWidth));
  }

  function persistChatWidth() {
    setChatWidth((w) => {
      window.localStorage.setItem(CHAT_WIDTH_STORAGE_KEY, String(Math.round(w)));
      return w;
    });
  }

  function handleSplitterKeyboardResize(deltaX: number) {
    applyChatWidthDelta(deltaX);
    persistChatWidth();
  }

  // Reset tabs and code view when projectId changes.
  useEffect(() => {
    setMobileTab('chat');
    setPreviewReady(false);
    setActiveCodeFile(null);
  }, [projectId]);

  const handlePreviewReady = useCallback(() => {
    setPreviewReady(true);
  }, []);

  function switchToPreview() {
    setMobileTab('preview');
    setPreviewReady(false);
  }

  const handleOpenFile = useCallback((file: CodeTab) => {
    setActiveCodeFile(file);
    if (isMobile) {
      setMobileTab('preview');
      setPreviewReady(false);
    }
  }, [isMobile]);

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !projectId) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{t('loadError')}</p>
          <a href="/dashboard" className="text-blue-600 hover:text-blue-700">
            {t('backToDashboard')}
          </a>
        </div>
      </div>
    );
  }

  // Visibility helpers (single panel instance, shown/hidden by CSS).
  const showChat = !isMobile || mobileTab === 'chat';
  const showPreview = !isMobile || mobileTab === 'preview';

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <ProjectHeader project={project} hasFiles={hasFiles} />

      {/* Mobile tab bar — only visible on viewports < 768px */}
      <div className="flex md:hidden border-b border-gray-200 bg-white shrink-0">
        <button
          onClick={() => { setMobileTab('chat'); }}
          className={`flex-1 py-2 text-sm font-medium transition-colors ${
            mobileTab === 'chat'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Chat
        </button>
        <button
          onClick={switchToPreview}
          className={`flex-1 py-2 text-sm font-medium transition-colors relative ${
            mobileTab === 'preview'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Preview
          {previewReady && mobileTab === 'chat' && (
            <span className="absolute top-1.5 right-6 inline-flex w-2 h-2 rounded-full bg-green-500" />
          )}
        </button>
      </div>

      <div className="flex-1 flex overflow-hidden" ref={splitContainerRef}>
        {/* Chat Panel — resizable width on desktop, full width on mobile */}
        <div
          className={`flex-col bg-gray-50 overflow-hidden ${showChat ? 'flex' : 'hidden'} ${isMobile ? 'w-full' : 'shrink-0'}`}
          style={isMobile ? undefined : { width: chatWidth }}
        >
          <ChatPanel projectId={projectId} onOpenFile={handleOpenFile} />
        </div>

        {/* Drag handle — desktop split view only; mobile switches panes via tabs above. */}
        {!isMobile && (
          <PaneSplitter
            ariaLabel={t('resizePanels')}
            ariaValueNow={chatWidth}
            ariaValueMin={CHAT_PANE_MIN}
            ariaValueMax={
              splitContainerRef.current
                ? splitContainerRef.current.clientWidth * CHAT_PANE_MAX_RATIO
                : chatWidth
            }
            onDrag={applyChatWidthDelta}
            onDragEnd={persistChatWidth}
            onKeyboardResize={handleSplitterKeyboardResize}
          />
        )}

        {/* Preview Panel */}
        <div
          className={`flex-col bg-white overflow-hidden ${showPreview ? 'flex' : 'hidden'} ${isMobile ? 'w-full' : 'flex-1'}`}
        >
          <PreviewPanel
            projectId={projectId}
            onSandboxRunning={handlePreviewReady}
            activeCodeFile={activeCodeFile}
            onActiveCodeFileChange={setActiveCodeFile}
          />
        </div>
      </div>
    </div>
  );
}
